import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { FetchAllBidItemsRequest } from '../../../../../../../../core/projects.actions';
import { allBidItems, siteDetails } from '../../../../../../../../core/projects.selectors';
import { takeUntil } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { ItemPostingsWrapperComponent } from '../item-postings-wrapper/item-postings-wrapper.component';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';

@Component({
  standalone: false,
  selector: 'app-add-item-posting',
  templateUrl: './add-item-posting.component.html',
  styleUrls: ['./add-item-posting.component.scss'],
})
export class AddItemPostingComponent implements OnInit, OnDestroy {
  itemPostingsList = [];
  filteredItemPostingsList = [];
  projectFilterOptions: { value: any; label: string }[] = [];
  form = new FormGroup({
    searchText: new FormControl(''),
    project: new FormControl(null),
  });
  focusedIn = false;
  searchText: string;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private projectsService: ProjectsService,
    private commonService: CommonService,
  ) {
  }

  ngOnInit() {
    this.store.select(siteDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(site => {
        this.store.dispatch(FetchAllBidItemsRequest({
          payload: {
            projectId: site.project.id,
            qp: {
              include: [
                {
                  bid_items: ['fields'],
                },
              ],
            },
          },
        }));
      });

    this.store.select(allBidItems)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((items) => {
        this.itemPostingsList = items;
        this.buildProjectFilterOptions();
        this.applyFilters();
      });

    this.form.controls.searchText.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(searchText => {
        this.searchText = searchText || '';
        this.applyFilters();
      });

    this.form.controls.project.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.applyFilters();
      });

    this.commonService.focusedIn
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          this.focusedIn = data;
          this.applyFilters();
        } else {
          this.filteredItemPostingsList = [];
          this.focusedIn = false;
        }
      });
  }

  private buildProjectFilterOptions() {
    const projectNames = [...new Set(
      this.itemPostingsList
        .map(item => item.source_entity_extra_attributes?.project_name)
        .filter(Boolean)
    )].sort();

    this.projectFilterOptions = [
      { value: null, label: 'All projects' },
      ...projectNames.map(name => ({ value: name, label: name })),
    ];
  }

  private applyFilters() {
    let items = this.itemPostingsList;

    const selectedProject = this.form.controls.project.value;
    if (selectedProject) {
      items = items.filter(item => item.source_entity_extra_attributes?.project_name === selectedProject);
    }

    if (this.searchText) {
      const text = this.searchText.toLowerCase();
      items = items.filter(item => {
        const attrs = item.source_entity_extra_attributes;
        return item.item?.toLowerCase().includes(text)
          || item.description?.toLowerCase().includes(text)
          || attrs?.project_name?.toLowerCase().includes(text)
          || attrs?.project_item_line_number?.toLowerCase().includes(text)
          || attrs?.contract_item_line_number?.toLowerCase().includes(text)
          || attrs?.supplemental_description?.toLowerCase().includes(text);
      });
    }

    this.filteredItemPostingsList = items;
  }

  get selectedProjectLabel(): string {
    const selected = this.form.controls.project.value;
    if (!selected) return 'All projects';
    const option = this.projectFilterOptions.find(o => o.value === selected);
    return option?.label || 'All projects';
  }

  selectProject(value: any) {
    this.form.controls.project.setValue(value);
  }

  selectItemPostings(itemPostings: any) {
    this.projectsService.openRightPanel.next({
      component: ItemPostingsWrapperComponent,
      data: {
        itemPostings,
      },
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
